/**
 * SRES · Project Health Dashboard
 * ═══════════════════════════════
 *
 * Redesigned Feb 2026 — professional, executive-first layout.
 *
 * Structure
 * ─────────
 *   Row 1 · Executive summary strip (5 tiles):
 *            Overall Health · CPR · JORC · NI 43-101 · SAMREC
 *   Row 2 · KPI counter row (Critical · Warnings · Completed · Pending)
 *   Row 3 · Compact module list (top items only) with per-row Fix Now
 *   Row 4 · Collapsible "Advanced diagnostics" (full module matrix +
 *            placeholder policy detail + generation metadata)
 *
 * Every tile & row is:
 *   – clickable (navigates to the corresponding wizard step)
 *   – hover-detailed (title tooltips)
 *   – shows last updated + owner + approval status when available
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Shield, ShieldCheck, ShieldWarning, ArrowRight, Warning,
  CheckCircle, XCircle, Circle, Placeholder, CaretDown, CaretRight,
  ClockCounterClockwise, User as UserIcon,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import SetupPropagationPanel from "@/pages/dashboard/SetupPropagationPanel";

// ── Status style lookup ─────────────────────────────────────────────
const STATUS_STYLES = {
  passed:        { icon: CheckCircle, tone: "text-emerald-600",
                    label: "Passed",       badge: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  warning:       { icon: Warning,     tone: "text-amber-600",
                    label: "Warning",      badge: "bg-amber-100 text-amber-800 border-amber-200" },
  blocked:       { icon: XCircle,     tone: "text-rose-600",
                    label: "Blocked",      badge: "bg-rose-100 text-rose-800 border-rose-200" },
  not_completed: { icon: Circle,      tone: "text-slate-400",
                    label: "Pending",      badge: "bg-slate-100 text-slate-600 border-slate-200" },
  placeholder:   { icon: Placeholder, tone: "text-purple-500",
                    label: "Placeholder",  badge: "bg-purple-100 text-purple-800 border-purple-200" },
};


// ── Small helpers ───────────────────────────────────────────────────
function healthColour(pct) {
  if (pct >= 90) return { bar: "#059669", text: "text-emerald-700", chip: "bg-emerald-50 border-emerald-200" };
  if (pct >= 60) return { bar: "#0284c7", text: "text-sky-700",     chip: "bg-sky-50 border-sky-200" };
  if (pct >= 30) return { bar: "#d97706", text: "text-amber-700",   chip: "bg-amber-50 border-amber-200" };
  return                { bar: "#e11d48", text: "text-rose-700",    chip: "bg-rose-50 border-rose-200" };
}


function fixNowHref(mod, projectId) {
  if (!mod?.fix_now) return `/app/projects/${projectId}/wizard`;
  const step  = mod.fix_now.wizard_step_key || "";
  const panel = mod.fix_now.panel ? `?panel=${encodeURIComponent(mod.fix_now.panel)}` : "";
  const focus = mod.fix_now.focus_field ? `${panel ? "&" : "?"}focus=${encodeURIComponent(mod.fix_now.focus_field)}` : "";
  const rule  = mod.fix_now.highlight_rule ? `&rule=${mod.fix_now.highlight_rule}` : "";
  return `/app/projects/${projectId}/wizard/${step}${panel}${focus}${rule}`;
}


// ── Executive summary tile (Overall Health) ─────────────────────────
function OverallHealthTile({ pct, readyForCpr }) {
  const col = healthColour(pct);
  return (
    <div className={`rounded-lg border ${col.chip} p-4 flex items-center gap-4`}
         data-testid="dash-overall-tile">
      <div className="relative w-20 h-20 shrink-0">
        <svg viewBox="0 0 36 36" className="-rotate-90 w-full h-full">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
          <circle cx="18" cy="18" r="15.9" fill="none"
                  stroke={col.bar} strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={`${pct}, 100`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-lg font-bold ${col.text}`} data-testid="dash-overall-pct">
            {pct.toFixed(0)}%
          </span>
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Project Health
        </div>
        <div className="text-[14px] font-bold text-slate-800 leading-tight mt-0.5">
          Overall readiness
        </div>
        <Badge className={
          readyForCpr
            ? "mt-1 bg-emerald-600 text-white text-[9px] tracking-widest"
            : "mt-1 bg-rose-600 text-white text-[9px] tracking-widest"
        } data-testid="dash-cpr-status">
          {readyForCpr ? "CPR READY" : "CPR BLOCKED"}
        </Badge>
      </div>
    </div>
  );
}


function StandardTile({ label, ready, testid, title }) {
  const Icon = ready ? ShieldCheck : ShieldWarning;
  const tone = ready
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : "border-slate-200 bg-slate-50 text-slate-500";
  return (
    <div className={`rounded-lg border p-4 flex flex-col gap-1 ${tone}`}
         title={title}
         data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon size={18} weight={ready ? "fill" : "regular"} />
        <span className={`text-[9px] font-mono tracking-widest px-1.5 py-0.5 rounded ${
          ready ? "bg-emerald-600 text-white" : "bg-slate-400 text-white"
        }`}>{ready ? "READY" : "LOCKED"}</span>
      </div>
      <div className="text-[13px] font-bold leading-tight mt-1">{label}</div>
      <div className="text-[10px] font-mono opacity-70 leading-tight">
        {ready ? "All checks pass" : "Awaiting inputs"}
      </div>
    </div>
  );
}


// ── KPI row (small counters) ────────────────────────────────────────
function KpiTile({ label, value, tone, icon, testid, onClick }) {
  const Icon = icon;
  return (
    <button onClick={onClick}
            className={`w-full text-left rounded-lg border px-3 py-2 flex items-center gap-3 transition hover:shadow-sm ${tone}`}
            data-testid={testid}>
      {Icon && <Icon size={20} weight="duotone" />}
      <div className="flex-1 min-w-0">
        <div className="text-xl font-bold leading-none">{value}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-80 mt-1">
          {label}
        </div>
      </div>
    </button>
  );
}


// ── Compact module row (used both in condensed list + full matrix) ──
function ModuleRow({ mod, projectId, compact = false }) {
  const s = STATUS_STYLES[mod.status] || STATUS_STYLES.not_completed;
  const Icon = s.icon;
  const canFix = mod.fix_now && mod.status !== "passed";
  return (
    <div className={`flex items-center gap-3 border-b border-slate-100 last:border-b-0 px-3 ${
        compact ? "py-1.5" : "py-2"
      } hover:bg-slate-50 transition`}
         data-testid={`dash-mod-${mod.key}`}>
      <Icon size={16} weight="fill" className={s.tone + " shrink-0"} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold text-slate-800 truncate">
            {mod.label}
          </span>
          {mod.cpr_mandatory && (
            <span className="text-[8px] font-mono tracking-widest text-slate-500 border border-slate-300 rounded px-1"
                  title="Required for CPR">CPR</span>
          )}
          {mod.blocking && (
            <span className="text-[8px] font-mono tracking-widest text-rose-700 bg-rose-100 border border-rose-200 rounded px-1"
                  data-testid={`dash-blocking-${mod.key}`}>BLOCKING</span>
          )}
        </div>
        <div className="text-[10px] font-mono text-slate-500 truncate"
             title={mod.recommended_action || ""}>
          step {mod.wizard_step} · {s.label}
          {mod.critical_count > 0 && ` · ${mod.critical_count} critical`}
          {mod.warning_count  > 0 && ` · ${mod.warning_count} warning`}
        </div>
      </div>

      <span className="text-[11px] font-mono text-slate-700 w-12 text-right shrink-0">
        {mod.completion_pct.toFixed(0)}%
      </span>

      {canFix ? (
        <Link to={fixNowHref(mod, projectId)}
              className="shrink-0 bg-brand-blue text-white text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded hover:bg-brand-blue/90 flex items-center gap-1"
              title={mod.fix_now.description || "Open in wizard"}
              data-testid={`dash-fix-${mod.key}`}>
          Fix now <ArrowRight size={10} />
        </Link>
      ) : (
        <Link to={`/app/projects/${projectId}/wizard`}
              className="shrink-0 text-slate-400 hover:text-slate-700"
              title="Open in wizard">
          <ArrowRight size={13} />
        </Link>
      )}
    </div>
  );
}


// ── Main dashboard ──────────────────────────────────────────────────
export default function ProjectHealthDashboard({ projectId }) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const q = useQuery({
    queryKey: ["project-health", projectId],
    queryFn: () => api.get(`/projects/${projectId}/health`).then((r) => r.data),
    enabled: !!projectId,
    staleTime: 20_000,
    refetchInterval: 30_000,
  });

  const attention = useMemo(() => {
    const mods = q.data?.modules || [];
    // rank blocking → critical count → warning count → not-completed
    const rank = (m) =>
      (m.blocking ? 0 : 100) +
      (m.status === "blocked" ? 0 : m.status === "warning" ? 10 : m.status === "not_completed" ? 20 : 90) -
      (m.critical_count || 0) * 3 -
      (m.warning_count  || 0);
    return [...mods].sort((a, b) => rank(a) - rank(b));
  }, [q.data]);

  if (q.isLoading) {
    return (
      <div className="border border-slate-200 rounded-lg bg-white p-6 animate-pulse"
           data-testid="project-health-loading">
        <div className="h-8 bg-slate-100 rounded mb-4 w-1/3" />
        <div className="grid grid-cols-5 gap-3 mb-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 bg-slate-100 rounded" />
          ))}
        </div>
        <div className="h-32 bg-slate-100 rounded" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="border border-rose-200 bg-rose-50 rounded-lg p-4 text-rose-800 text-sm"
           data-testid="project-health-error">
        Failed to load project health. {q.error?.message}
      </div>
    );
  }

  const h = q.data;
  const topAttention = attention.filter((m) => m.status !== "passed").slice(0, 6);

  return (
    <div data-testid="dashboard" className="contents">
    <div className="border border-slate-200 rounded-lg bg-white shadow-sm overflow-hidden"
         data-testid="project-health-dashboard">

      {/* ── Header ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 bg-slate-50/60">
        <div className="flex items-center gap-2">
          <Shield size={20} className="text-brand-blue" weight="duotone" />
          <h2 className="text-[15px] font-bold text-slate-800">Project Health</h2>
          <span className="text-[10px] font-mono text-slate-400"
                title="Generated timestamp">
            <ClockCounterClockwise size={11} weight="regular" className="inline mr-0.5" />
            {new Date(h.generated_at).toLocaleString()}
          </span>
        </div>
        <div className="text-[10px] font-mono text-slate-500 hidden md:block">
          {h.modules.length} modules · {h.modules.filter((m) => m.cpr_mandatory).length} required for CPR
        </div>
      </div>

      {/* ── Row 1 · Executive summary (5 tiles) ─────────────── */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
           data-testid="dash-exec-row">
        <OverallHealthTile pct={h.overall_pct} readyForCpr={h.ready_for_cpr} />
        <StandardTile label="CPR Readiness"
                       ready={h.ready_for_cpr}
                       testid="dash-cpr-tile"
                       title="Competent Persons Report — every mandatory module must pass." />
        <StandardTile label="JORC 2012"
                       ready={h.ready_for_jorc}
                       testid="dash-jorc-tile"
                       title="Joint Ore Reserves Committee code (Australia)" />
        <StandardTile label="NI 43-101"
                       ready={h.ready_for_ni43101}
                       testid="dash-ni43101-tile"
                       title="Canadian Securities Administrators — National Instrument 43-101" />
        <StandardTile label="SAMREC"
                       ready={h.ready_for_samrec}
                       testid="dash-samrec-tile"
                       title="South African Mineral Resource Committee" />
      </div>

      {/* ── Row 2 · KPI counters ──────────────────────────────── */}
      <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-3"
           data-testid="dash-kpi-row">
        <KpiTile label="Critical errors" value={h.critical_errors}
                 tone="bg-rose-50 border-rose-200 text-rose-800"
                 icon={XCircle}
                 testid="dash-kpi-critical"
                 onClick={() => setDetailsOpen(true)} />
        <KpiTile label="Warnings"        value={h.warnings}
                 tone="bg-amber-50 border-amber-200 text-amber-800"
                 icon={Warning}
                 testid="dash-kpi-warnings"
                 onClick={() => setDetailsOpen(true)} />
        <KpiTile label="Completed"       value={h.completed}
                 tone="bg-emerald-50 border-emerald-200 text-emerald-800"
                 icon={CheckCircle}
                 testid="dash-kpi-completed"
                 onClick={() => setDetailsOpen(true)} />
        <KpiTile label="Pending"         value={h.pending}
                 tone="bg-slate-50 border-slate-200 text-slate-700"
                 icon={Circle}
                 testid="dash-kpi-pending"
                 onClick={() => setDetailsOpen(true)} />
      </div>

      {/* ── Row 3 · Top items needing attention ───────────────── */}
      <div className="px-4 pb-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
            Needs attention
          </div>
          {h.placeholder_flags?.length > 0 && (
            <div className="text-[10px] text-purple-700 flex items-center gap-1"
                 data-testid="dash-placeholder-flag"
                 title="Zero-Placeholder Policy — real inputs required">
              <Placeholder size={11} weight="fill" />
              {h.placeholder_flags.length} placeholder{h.placeholder_flags.length > 1 ? "s" : ""}
            </div>
          )}
        </div>

        {topAttention.length === 0 ? (
          <div className="rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-[12px] font-mono p-3 flex items-center gap-2"
               data-testid="dash-attention-empty">
            <CheckCircle size={16} weight="fill" className="text-emerald-600" />
            All modules pass their technical checks.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden"
               data-testid="dash-attention-list">
            {topAttention.map((m) => (
              <ModuleRow key={m.key} mod={m} projectId={projectId} />
            ))}
          </div>
        )}
      </div>

      {/* ── Row 4 · Collapsible Full Diagnostics ──────────────── */}
      <div className="border-t border-slate-200">
        <button onClick={() => setDetailsOpen((v) => !v)}
                data-testid="dash-details-toggle"
                className="w-full flex items-center justify-between px-5 py-2 text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition">
          <span className="flex items-center gap-2">
            {detailsOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
            Full module matrix &amp; diagnostics
          </span>
          <span className="text-slate-400">
            {h.modules.length} modules
          </span>
        </button>

        {detailsOpen && (
          <div className="px-4 pb-4 pt-1" data-testid="dash-details-panel">
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {h.modules.map((m) => (
                <ModuleRow key={m.key} mod={m} projectId={projectId} compact />
              ))}
            </div>

            {/* Zero-placeholder policy detail */}
            {h.placeholder_flags?.length > 0 && (
              <div className="mt-3 rounded border border-purple-200 bg-purple-50 p-3"
                   data-testid="dash-placeholder-detail">
                <div className="text-[11px] font-mono uppercase tracking-widest text-purple-700 flex items-center gap-1 mb-1">
                  <Placeholder size={12} weight="fill" />
                  Zero-Placeholder Policy
                </div>
                <div className="text-[11px] text-purple-800">
                  The following modules are still using placeholder defaults —
                  a CPR cannot be finalised until every one is replaced with
                  project-specific data:
                </div>
                <ul className="mt-1 text-[11px] font-mono text-purple-800 space-y-0.5">
                  {h.placeholder_flags.map((f) => (
                    <li key={f}>· {f}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 text-[10px] font-mono text-slate-500 flex items-center justify-between">
              <span>
                <ClockCounterClockwise size={11} className="inline mr-0.5" />
                Generated {new Date(h.generated_at).toLocaleString()}
              </span>
              <span>
                Reporting standards: JORC 2012 · NI 43-101 · SAMREC · CRIRSCO
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sprint P32 · Setup Propagation audit trace */}
      <div className="mt-4">
        <SetupPropagationPanel projectId={projectId} />
      </div>

    </div>
    </div>
  );
}
